/**
 * La agenda de Donna en el Google Calendar (u otro calendario) de la propia clínica o del profesional.
 * Lógica pura: qué dice cada evento según la privacidad elegida y el archivo .ics de la suscripción.
 * Por defecto NO sale ningún dato clínico (ni motivo de la cita ni notas): solo quién, cuándo y dónde.
 */

export const PRIVACIDADES_AGENDA = [
  ["nombre", "Nombre y apellido del paciente"],
  ["iniciales", "Solo las iniciales del paciente"],
  ["ocupado", "Solo «Cita» (sin ningún dato del paciente)"],
] as const;
export type PrivacidadAgenda = (typeof PRIVACIDADES_AGENDA)[number][0];
export const esPrivacidadAgenda = (v: unknown): v is PrivacidadAgenda => PRIVACIDADES_AGENDA.some(([k]) => k === v);

export interface CitaAgenda {
  id: string; inicio: Date | string; fin: Date | string; estado: string;
  nombres: string; apellidos: string; pacienteId: string;
  profesional: string; sede: string | null; direccion: string | null; conSedes: boolean;
}
export interface EventoAgenda { id: string; inicio: Date; fin: Date; titulo: string; descripcion: string; lugar: string; confirmada: boolean }

const palabra = (s: string) => s.trim().split(/\s+/)[0] ?? "";
/** «Lucía María» «Paredes Mora» → «L. P.» */
export const inicialesDe = (nombres: string, apellidos: string) =>
  [palabra(nombres), palabra(apellidos)].filter(Boolean).map((p) => p[0].toUpperCase() + ".").join(" ");
/** «Lucía María» «Paredes Mora» → «Lucía Paredes» (el primer nombre y el primer apellido). */
export const nombreCortoDe = (nombres: string, apellidos: string) => [palabra(nombres), palabra(apellidos)].filter(Boolean).join(" ");

export function tituloAgenda(p: PrivacidadAgenda, nombres: string, apellidos: string): string {
  if (p === "ocupado") return "Cita";
  if (p === "iniciales") return inicialesDe(nombres, apellidos) || "Cita";
  return nombreCortoDe(nombres, apellidos) || "Cita";
}

export function eventoDeAgenda(c: CitaAgenda, o: { privacidad: PrivacidadAgenda; mostrarProfesional: boolean; urlPublica?: string }): EventoAgenda {
  const confirmada = c.estado === "confirmada" || c.estado === "atendida";
  const lineas: string[] = [];
  if (o.mostrarProfesional && c.profesional) lineas.push(`Profesional: ${c.profesional}`);
  if (o.privacidad !== "ocupado") lineas.push(`Estado: ${c.estado === "atendida" ? "atendida" : c.estado === "no_asistio" ? "no asistió" : confirmada ? "confirmada" : "por confirmar"}`);
  if (o.privacidad === "nombre" && o.urlPublica) lineas.push(`Ficha en Donna: ${o.urlPublica.replace(/\/$/, "")}/#/paciente/${c.pacienteId}`);
  const lugar = [c.conSedes && c.sede ? c.sede : "", c.direccion ?? ""].filter(Boolean).join(" · ");
  return { id: c.id, inicio: new Date(c.inicio), fin: new Date(c.fin), titulo: tituloAgenda(o.privacidad, c.nombres, c.apellidos), descripcion: lineas.join("\n"), lugar, confirmada };
}

const utc = (d: Date) => d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");
const escapar = (t: string) => t.replace(/\\/g, "\\\\").replace(/;/g, "\\;").replace(/,/g, "\\,").replace(/\r?\n/g, "\\n");

/** Cada línea mide como máximo 75 bytes (RFC 5545); lo que sobra sigue en la línea siguiente con un espacio al inicio. */
export function doblarLinea(linea: string): string {
  const partes: string[] = [];
  let actual = "", bytes = 0, limite = 75;
  for (const ch of linea) {
    const b = Buffer.byteLength(ch, "utf8");
    if (bytes + b > limite) { partes.push(actual); actual = " "; bytes = 1; limite = 75; }
    actual += ch; bytes += b;
  }
  partes.push(actual);
  return partes.join("\r\n");
}

/** El calendario completo (.ics) al que se suscribe Google Calendar. */
export function icsAgenda(nombre: string, eventos: EventoAgenda[], ahora = new Date()): string {
  const lineas = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//Donna//Agenda//ES", "CALSCALE:GREGORIAN", "METHOD:PUBLISH",
    `X-WR-CALNAME:${escapar(nombre)}`, "X-WR-TIMEZONE:America/Guayaquil", "REFRESH-INTERVAL;VALUE=DURATION:PT1H", "X-PUBLISHED-TTL:PT1H",
  ];
  for (const e of eventos) {
    lineas.push("BEGIN:VEVENT", `UID:cita-${e.id}@donna`, `DTSTAMP:${utc(ahora)}`, `DTSTART:${utc(e.inicio)}`, `DTEND:${utc(e.fin)}`, `SUMMARY:${escapar(e.titulo)}`);
    if (e.descripcion) lineas.push(`DESCRIPTION:${escapar(e.descripcion)}`);
    if (e.lugar) lineas.push(`LOCATION:${escapar(e.lugar)}`);
    lineas.push(`STATUS:${e.confirmada ? "CONFIRMED" : "TENTATIVE"}`, "TRANSP:OPAQUE", "END:VEVENT");
  }
  lineas.push("END:VCALENDAR");
  return lineas.map(doblarLinea).join("\r\n") + "\r\n";
}

/** Identificador del evento en Google: letras a-v y números (base32hex), y un mínimo de 5 caracteres. Es el de la cita sin guiones. */
export const idEventoGoogle = (citaId: string) => "d" + citaId.replace(/-/g, "").toLowerCase();

/** Cuerpo del evento para la API de Google Calendar. */
export function eventoParaGoogle(e: EventoAgenda) {
  return {
    id: idEventoGoogle(e.id), summary: e.titulo, description: e.descripcion, location: e.lugar,
    start: { dateTime: e.inicio.toISOString(), timeZone: "America/Guayaquil" }, end: { dateTime: e.fin.toISOString(), timeZone: "America/Guayaquil" },
    status: e.confirmada ? "confirmed" : "tentative", transparency: "opaque", reminders: { useDefault: true },
  };
}
